import { Box, FormHelperText, Grow, SxProps, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";

import { JSONContent, HTMLContent, useEditor } from "@tiptap/react";
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
} from "react";

import useIsRefFocused from "../hooks/useIsRefFocused";
import { Attachment, UploadAttachment } from "protogen/common_pb";
import StyledEditorContent from "./StyledEditorContent";
import EditActions from "./EditActions";
import {
  addImagePlaceholder,
  extractImageReferences,
  generateRandomName,
  getExtensions,
  PLACEHOLDER_PATH,
  renderInitialContent,
} from "./utils";
import { convertHEICImages, downsizeImages } from "common/imageUtils";
import DragNDrop from "../common/DragNDrop";
import { useUploader } from "../creation/FileUploader";
import { PlainMessage } from "@bufbuild/protobuf";
import { RichContent } from "./utils";
import { Transaction } from "@tiptap/pm/state";
import { Editor } from "@tiptap/core/dist/packages/core/src";
import { Editor as ReactEditor } from "@tiptap/react";
import AttachmentList from "./AttachmentList";
import UploadAttachmentIcon from "./UploadAttachmentIcon";
import { InsertableSuggestion, insertSuggestion } from "./extensions/utils";
import useIsMobile from "../hooks/useIsMobile";
import { useGetUrlContent } from "services/files";
import { isUserAdvisor } from "../../common/userUtils";
import { CurrentUserContext } from "../context/RequireAuth";
import { Check, Loader } from "lucide-react";
import { AutoSaveStatus } from "./useAutoSave";
import { PasteHandler } from "./PasteHandler";

type Props = {
  setContent: (content: RichContent) => void;
  initialContent?: string | JSONContent | HTMLContent;
  initialAttachments?: Attachment[];
  placeholder?: string;
  passiveEditor?: boolean;
  leftPrimaryAction?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  editActions?: ReactNode[];
  disabled?: boolean;
  editorMinHeight?: string;
  editorMaxHeight?: string;
  editorDefaultFontSize?: string;
  scrollable?: boolean;
  // If present, attachments will be enabled.
  attachmentsEnabled?: boolean;
  setDragState?: (dragging: boolean) => void;
  sx?: SxProps;
  formError?: boolean;
  formHelperText?: string;
  placement?: "top" | "bottom";
  autofocus?: boolean;
  attachmentListSuffix?: ReactNode;
  linkUnfurlEnabled?: boolean;
  autoSaveStatus?: AutoSaveStatus;
  onUploadStateChange?: (state: {
    uploadsInProgress: boolean;
    uploadPercentage: number;
  }) => void;
  rightActions?: ReactNode[];
  imageOptions?: {
    showDownload?: boolean;
    showZoom?: boolean;
    showModal?: boolean;
  };
};
export type Handle = {
  updateAttachments: (attachments: Attachment[]) => void;
  insertSuggestion: (s: InsertableSuggestion) => void;
  getContent: () => RichContent | null;
  focus?: () => void;
};

const AuthSaveStatus = ({
  autoSaveStatus,
}: {
  autoSaveStatus: AutoSaveStatus;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        gap: "4px",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          opacity: autoSaveStatus === AutoSaveStatus.IDLE ? 0 : 1,
          transitionDelay: "100ms",
          transition: "opacity 200ms ease-in-out",
          display: "flex",
          gap: "4px",
          alignItems: "center",
        }}
      >
        {autoSaveStatus === AutoSaveStatus.SAVING ? (
          <>
            <Box
              sx={{
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                animation: "spin 2.5s linear infinite",
                "@keyframes spin": {
                  "0%": {
                    transform: "rotate(360deg)",
                  },
                  "100%": {
                    transform: "rotate(0deg)",
                  },
                },
              }}
            >
              <Loader size={16} color="#8E9598" />
            </Box>
            <Typography variant="bodySmall" color="text.tertiary">
              Saving...
            </Typography>
          </>
        ) : (
          <>
            <Check size={16} color="#8E9598" />
            <Typography variant="bodySmall" color="text.tertiary">
              Changes saved
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
const EditorActions = ({
  editor,
  showEditor,
  leftPrimaryAction,
  primaryAction,
  secondaryAction,
  extraActions,
  placement = "bottom",
  autoSaveStatus,
  rightActions,
}: {
  editor: ReactEditor;
  showEditor: boolean;
  extraActions: ReactNode[];
  leftPrimaryAction?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  placement?: "top" | "bottom";
  autoSaveStatus?: AutoSaveStatus;
  rightActions?: ReactNode[];
}) => {
  const isMobile = useIsMobile();
  return (
    <Grow in={showEditor}>
      <Box
        className={"editor-actions"}
        sx={{
          display: showEditor ? "flex" : "none",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          rowGap: "16px",
          marginTop: placement === "bottom" ? "20px" : "0px",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            paddingLeft: "0px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              width: isMobile ? "100%" : "10px",
              overflowX: "auto",
              display: "flex",
              alignItems: isMobile ? "end" : "center",
              flexDirection: isMobile ? "column-reverse" : "row",
              gap: isMobile ? "8px" : "0px",
            }}
          >
            {leftPrimaryAction}
            <Box
              sx={{
                flexGrow: 1,
                width: isMobile ? "100%" : "10px",
                overflowX: "auto",
                display: "flex",
                flexDirection: "row",
                minWidth: 0,
              }}
            >
              <EditActions
                editor={editor}
                extraActions={extraActions}
                position="left"
              />
            </Box>

            {/* Save indicator */}
            {!isMobile && autoSaveStatus && (
              <AuthSaveStatus autoSaveStatus={autoSaveStatus} />
            )}
          </Box>
          {rightActions && rightActions.filter(Boolean).length > 0 && (
            <EditActions
              editor={editor}
              extraActions={rightActions}
              position="right"
            />
          )}
        </Box>
        {(secondaryAction || primaryAction) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "12px",
              justifyContent: "flex-end",
              alignItems: "center",
              minWidth: "fit-content",
              ...(isMobile ? { width: "100%" } : {}),
            }}
          >
            {secondaryAction}
            {primaryAction}
          </Box>
        )}
      </Box>
    </Grow>
  );
};

export default forwardRef<Handle, Props>(
  (
    {
      setContent,
      initialContent,
      placeholder,
      passiveEditor = false,
      leftPrimaryAction,
      primaryAction,
      secondaryAction,
      editActions,
      disabled,
      editorMinHeight,
      editorMaxHeight,
      editorDefaultFontSize,
      scrollable,
      setDragState,
      initialAttachments = [],
      attachmentsEnabled = false,
      sx,
      formError = false,
      formHelperText,
      placement = "bottom",
      autofocus = false,
      attachmentListSuffix,
      linkUnfurlEnabled = false,
      autoSaveStatus,
      onUploadStateChange,
      rightActions,
      imageOptions,
    }: Props,
    ref,
  ) => {
    const notesRef = React.useRef<HTMLDivElement>(null);
    const currentUser = useContext(CurrentUserContext);
    const { isFocused } = useIsRefFocused(notesRef);
    const {
      onUpload,
      fileUploads,
      fileOnlyUploads,
      uploadPercentage,
      removeUpload,
      getCompleteAttachments,
      withAttachments,
      uploadsInProgress,
      updateFileUpload,
    } = useUploader({
      initialAttachments: initialAttachments,
    });

    const { request: getUrlContentRequest } = useGetUrlContent();

    const updateAttachments = (attachments: Attachment[]) => {
      withAttachments(attachments);
      if (!editor) return;
      const attachmentMap = attachments.reduce(
        (mapping, a) => {
          mapping[a.inlineReference] = a;
          return mapping;
        },
        {} as { [inlineReference: string]: Attachment },
      );
      editor.state.doc.descendants((a, startPos) => {
        if (
          a.type.name === "image" &&
          attachmentMap[a.attrs.title] &&
          (a.attrs.src === PLACEHOLDER_PATH ||
            // Don't replace if it's a data URL, would disrupt the user experience.
            // || a.attrs.src.startsWith('data:')
            a.attrs.src === "")
        ) {
          const n = attachmentMap[a.attrs.title];
          const newNode = editor.schema.nodes.image.create({
            title: n.inlineReference,
            src: n.url,
          });
          const transaction = editor.state.tr.replaceWith(
            startPos,
            startPos + a.nodeSize,
            newNode,
          );
          return editor.view.dispatch(transaction);
        }
      });
    };
    useImperativeHandle(ref, () => ({
      updateAttachments: updateAttachments,
      insertSuggestion: (s: InsertableSuggestion) =>
        editor && insertSuggestion(editor, s),
      getContent: () => {
        if (!editor) return null;
        const references = extractImageReferences(editor.getJSON());
        const attachments = getCompleteAttachments();
        const filteredAttachments = attachments.filter(
          (a) => !a.inlineReference || references.has(a.inlineReference),
        );
        return {
          html: editor.getHTML() || "",
          text: editor.getText({ blockSeparator: "\n" }) || "",
          json: JSON.stringify(editor.getJSON()),
          attachments: filteredAttachments,
        };
      },
      focus: () => {
        if (!editor) return;
        if (editor.isFocused) return;
        const endPos = editor.state.doc.content.size;
        editor.chain().focus().setTextSelection(endPos).run();
      },
    }));

    const onUpdate = (props: { editor: Editor; transaction: Transaction }) => {
      if (props.transaction.steps.length === 1) {
        // @ts-ignore
        const slice = props.transaction?.steps[0]?.slice;
        if (
          slice?.content?.content?.length === 1 &&
          slice.content.content[0].type.name === "image" &&
          slice.content.content[0].attrs.src.startsWith("https://s3")
        ) {
          // This is a placeholder image, don't update content
          return;
        }
      }
      const references = extractImageReferences(props.editor.getJSON());
      const attachments = getCompleteAttachments();
      const filteredAttachments = attachments.filter(
        (a) => !a.inlineReference || references.has(a.inlineReference),
      );
      setContent({
        html: props.editor.getHTML(),
        text: props.editor.getText({ blockSeparator: "\n" }),
        json: JSON.stringify(props.editor.getJSON()),
        attachments: filteredAttachments,
      });
    };

    const editor: Editor | null = useEditor({
      editable: !disabled,
      extensions: getExtensions({
        placeholder,
        handleCommunications: currentUser && isUserAdvisor(currentUser),
        linkUnfurlEnabled: linkUnfurlEnabled,
        imageOptions: imageOptions,
      }),
      content: renderInitialContent(initialContent, initialAttachments),
      onUpdate: onUpdate,
      editorProps: {
        handlePaste: (_, event): boolean =>
          PasteHandler(_, event, {
            editor,
            onUpload,
            setAttachments,
            getContentRequest: getUrlContentRequest,
            updateFileUpload,
            inlineAttachmentsEnabled: true,
          }),
      },
      parseOptions: {
        preserveWhitespace: "full",
      },
      autofocus: autofocus,
    });

    useEffect(() => {
      if (editor) {
        editor.setOptions({
          onUpdate: onUpdate,
          editorProps: {
            handlePaste: (_, event): boolean =>
              PasteHandler(_, event, {
                editor,
                onUpload,
                setAttachments,
                getContentRequest: getUrlContentRequest,
                updateFileUpload,
                inlineAttachmentsEnabled: true,
              }),
          },
        });
      }
    }, [editor, fileUploads, onUpdate]);

    // Use the useCallback here because draft can change while we are uploading so we need to make sure it isn't stale.
    // Upon further reflection, I'm not sure I need useCallback nor if the state I'm listening to is relevant. This
    // is a duct tape solution that seems to be fine.
    const setAttachments = useCallback(
      (attachments: PlainMessage<UploadAttachment>[]) => {
        if (editor) {
          setContent({
            html: editor.getHTML(),
            text: editor.getText({ blockSeparator: "\n" }),
            json: JSON.stringify(editor.getJSON()),
            attachments: attachments,
          });
        }
      },
      [editor, fileUploads],
    );

    useEffect(() => {
      onUploadStateChange?.({ uploadsInProgress, uploadPercentage });
    }, [uploadsInProgress, uploadPercentage, onUploadStateChange]);

    if (!editor) return null;
    const onUploadInlineFiles = (files: FileList | null) => {
      if (!files) return;
      const inlineReferences = [];
      (async () => {
        const convertedFiles = await convertHEICImages(Array.from(files));
        const resizedFiles = await downsizeImages(
          convertedFiles,
          null,
          null,
          800,
        );
        for (let i = 0; i < resizedFiles.length; i++) {
          if (resizedFiles[i].file.type.startsWith("image/")) {
            // maybe configure this by product? notes/etc.
            const name = generateRandomName("file-");
            addImagePlaceholder(
              editor,
              name,
              resizedFiles[i].file,
              true,
              resizedFiles[i].resized.toString(),
            );
            inlineReferences.push(name);
          } else {
            inlineReferences.push(null);
          }
        }
        onUpload(
          resizedFiles.map((res) => res.file),
          inlineReferences,
          setAttachments,
        );
      })();
    };

    const extraActions = [
      ...(attachmentsEnabled
        ? [
            <UploadAttachmentIcon
              uploadsInProgress={uploadsInProgress}
              uploadPercentage={uploadPercentage}
              onChange={(files) => {
                (async () => {
                  const convertedFiles = await convertHEICImages(
                    Array.from(files || []),
                  );
                  onUpload(convertedFiles, undefined, setAttachments);
                })();
              }}
            />,
            <UploadAttachmentIcon
              imageIcon
              uploadsInProgress={uploadsInProgress}
              uploadPercentage={uploadPercentage}
              onChange={onUploadInlineFiles}
            />,
          ]
        : []),
      ...(editActions || []),
    ];

    const showEditor = !passiveEditor || isFocused;
    return (
      <DragNDrop
        enabled={attachmentsEnabled}
        setHover={setDragState}
        onUpload={onUploadInlineFiles}
      >
        <Box
          ref={notesRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "10px",
            fontWeight: 500,
            ...sx,
          }}
        >
          {placement === "top" && (
            <EditorActions
              editor={editor}
              showEditor={showEditor}
              leftPrimaryAction={leftPrimaryAction}
              primaryAction={primaryAction}
              secondaryAction={secondaryAction}
              extraActions={extraActions}
              placement={placement}
              autoSaveStatus={autoSaveStatus}
              rightActions={rightActions}
            />
          )}
          {showEditor && placement === "top" && (
            <Divider sx={{ marginTop: "4px" }} />
          )}
          <Box
            sx={{
              display: "flex",
              paddingLeft: "0px",
              paddingRight: "0px",
              marginTop: "16px",
            }}
          >
            <StyledEditorContent
              minHeight={editorMinHeight}
              maxHeight={editorMaxHeight}
              fontSize={editorDefaultFontSize}
              scrollable={scrollable}
              editor={editor}
            />
          </Box>
          <AttachmentList
            attachments={fileOnlyUploads}
            onDelete={(filename) => removeUpload(filename, setAttachments)}
            attachmentListSuffix={attachmentListSuffix}
          />
          {formHelperText && (
            <FormHelperText error={formError}>{formHelperText}</FormHelperText>
          )}
          {showEditor && (!placement || placement === "bottom") && <Divider />}
          {!placement ||
            (placement === "bottom" && (
              <EditorActions
                editor={editor}
                showEditor={showEditor}
                leftPrimaryAction={leftPrimaryAction}
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                extraActions={extraActions}
                placement={placement}
                autoSaveStatus={autoSaveStatus}
              />
            ))}
        </Box>
      </DragNDrop>
    );
  },
);
